import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getUnderDeliveredAddresses, fixUnderDeliveredAddresses } from "../api/addresses";
import type { UnderDeliveredAddress } from "../api/addresses/types";
import { showError, showSuccess } from "../shared/toast";
import "./hidden-addresses-panel.css";

const QUERY_KEY = ["addresses", "under-delivered"];

interface PostalGroup {
    postalCode: string;
    city: string;
    rows: UnderDeliveredAddress[];
}

function rowKey(row: { id: string; campaignId: string }): string {
    return `${row.id}:${row.campaignId}`;
}

function formatAddressLine(a: { street: string; number?: string | null; apartment?: string | null }): string {
    const parts = [a.street, a.number].filter(Boolean).join(" ");
    return a.apartment ? `${parts}, ${a.apartment}` : parts;
}

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString("sv-SE", { year: "numeric", month: "short", day: "numeric" });
}

function groupByPostalCode(rows: UnderDeliveredAddress[]): PostalGroup[] {
    const groups = new Map<string, PostalGroup>();
    for (const row of rows) {
        const key = row.postalCode || "Okänt postnummer";
        let group = groups.get(key);
        if (!group) {
            group = { postalCode: key, city: row.city, rows: [] };
            groups.set(key, group);
        }
        group.rows.push(row);
    }
    return Array.from(groups.values()).sort((a, b) => a.postalCode.localeCompare(b.postalCode, "sv"));
}

export const UnderDeliveredAddressesPanel = (): JSX.Element => {
    const queryClient = useQueryClient();
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const { data: rows = [], isLoading, error } = useQuery({
        queryKey: QUERY_KEY,
        queryFn: getUnderDeliveredAddresses
    });

    const groups = useMemo(() => groupByPostalCode(rows), [rows]);

    const clearSelectionAndRefresh = async () => {
        setSelected(new Set());
        await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    };

    const fixMutation = useMutation({
        mutationFn: fixUnderDeliveredAddresses,
        onSuccess: async (data) => {
            showSuccess(`${data.fixedRowCount} adresser rättade (${data.updatedDeliveryCount} utdelningar uppdaterade)`);
            await clearSelectionAndRefresh();
        },
        onError: (e) => {
            const msg = e instanceof Error ? e.message : "Okänt fel";
            showError("Kunde inte rätta utdelningarna", msg);
        }
    });

    const toggleRow = (key: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const toggleGroup = (group: PostalGroup) => {
        const keys = group.rows.map(rowKey);
        const allSelected = keys.every((k) => selected.has(k));
        setSelected((prev) => {
            const next = new Set(prev);
            keys.forEach((k) => (allSelected ? next.delete(k) : next.add(k)));
            return next;
        });
    };

    const toggleAll = () => {
        setSelected((prev) => (prev.size === rows.length ? new Set() : new Set(rows.map(rowKey))));
    };

    const selectedRows = useMemo(
        () => rows.filter((row) => selected.has(rowKey(row))),
        [rows, selected]
    );

    const handleFix = (row: UnderDeliveredAddress) => {
        const confirmed = window.confirm(
            `Sätt utdelat till ${row.deliveryUnitCount} lådor för ${formatAddressLine(row)}, kampanj "${row.campaignName}"? Detta uppdaterar loggade utdelningar i databasen.`
        );
        if (confirmed) {
            fixMutation.mutate([{ addressId: row.id, campaignId: row.campaignId }]);
        }
    };

    const handleBulkFix = () => {
        if (selected.size === 0) {
            return;
        }
        const confirmed = window.confirm(
            `Rätta ${selected.size} rader genom att sätta utdelat till respektive lådantal? Detta uppdaterar loggade utdelningar i databasen.`
        );
        if (confirmed) {
            fixMutation.mutate(selectedRows.map((row) => ({ addressId: row.id, campaignId: row.campaignId })));
        }
    };

    if (error) {
        return (
            <div className="hidden-addresses-panel">
                <h2>Underleveranser</h2>
                <p className="error-message">Kunde inte ladda adresser.</p>
            </div>
        );
    }

    return (
        <section className="hidden-addresses-panel" aria-label="Underleveranser">
            <div className="hidden-addresses-panel__header">
                <h2>Underleveranser</h2>
                <p className="hidden-addresses-panel__description">
                    Synliga adresser med fler lådor än vad som är loggat som utdelat i en kampanj. Uppstår ofta när en adress
                    redigerats eller slagits ihop i efterhand utan att utdelningen loggats om. Rätta sätter utdelat till
                    adressens aktuella lådantal i databasen.
                </p>
            </div>

            {isLoading ? (
                <p>Laddar...</p>
            ) : rows.length === 0 ? (
                <p className="hidden-addresses-panel__empty">Inga adresser med underleverans hittades.</p>
            ) : (
                <>
                    <div className="hidden-addresses-panel__toolbar">
                        <label className="hidden-addresses-panel__select-all">
                            <input
                                type="checkbox"
                                checked={selected.size > 0 && selected.size === rows.length}
                                onChange={toggleAll}
                            />
                            Markera alla ({rows.length})
                        </label>
                        {selected.size > 0 && (
                            <div className="hidden-addresses-panel__bulk-actions">
                                <span>{selected.size} rader valda</span>
                                <button
                                    className="action-button action-button--approve"
                                    onClick={handleBulkFix}
                                    disabled={fixMutation.isPending}
                                >
                                    Rätta valda
                                </button>
                            </div>
                        )}
                    </div>

                    {groups.map((group) => {
                        const keys = group.rows.map(rowKey);
                        const groupSelectedCount = keys.filter((k) => selected.has(k)).length;
                        return (
                            <div className="hidden-addresses-panel__group" key={group.postalCode}>
                                <div className="hidden-addresses-panel__group-header">
                                    <label className="hidden-addresses-panel__group-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={groupSelectedCount === keys.length}
                                            ref={(el) => {
                                                if (el) {
                                                    el.indeterminate = groupSelectedCount > 0 && groupSelectedCount < keys.length;
                                                }
                                            }}
                                            onChange={() => toggleGroup(group)}
                                        />
                                        <strong>{group.postalCode}</strong> {group.city}
                                    </label>
                                    <span className="hidden-addresses-panel__group-meta">{group.rows.length} rader</span>
                                </div>
                                <div className="hidden-addresses-panel__list">
                                    <table className="hidden-addresses-table">
                                        <thead>
                                            <tr>
                                                <th></th>
                                                <th>Adress</th>
                                                <th>Kampanj</th>
                                                <th>Antal lådor</th>
                                                <th>Utdelat</th>
                                                <th>Saknas</th>
                                                <th>Senaste utdelning</th>
                                                <th>Karta</th>
                                                <th>Åtgärd</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {group.rows.map((row) => (
                                                <tr key={rowKey(row)}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={selected.has(rowKey(row))}
                                                            onChange={() => toggleRow(rowKey(row))}
                                                            aria-label={`Markera ${formatAddressLine(row)}`}
                                                        />
                                                    </td>
                                                    <td>{formatAddressLine(row)}</td>
                                                    <td>{row.campaignName}</td>
                                                    <td>{row.deliveryUnitCount}</td>
                                                    <td>{row.deliveredQuantity}</td>
                                                    <td>{row.deliveryUnitCount - row.deliveredQuantity}</td>
                                                    <td>{formatDate(row.lastDeliveredAt)}</td>
                                                    <td>
                                                        <a
                                                            href={`https://www.google.com/maps/search/?api=1&query=${row.latitude},${row.longitude}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            Visa
                                                        </a>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="action-button action-button--approve"
                                                            onClick={() => handleFix(row)}
                                                            disabled={fixMutation.isPending}
                                                        >
                                                            Rätta
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        );
                    })}
                </>
            )}
        </section>
    );
};
